<template>
  <!-- TODO: move this out of components-rel -->
  <q-dialog ref="dialogRef" class="rel-dialog-edit-signature" data-test="ConfirmDialog" @hide="onDialogHide">
    <q-card class="r-pa-10">
      <q-card-section class="h2-desktop q-mb-md q-pa-none">
        Edit Email Signature
      </q-card-section>
      
      <q-card-section class="q-mb-md q-pa-none">
        <RelCardInfo class="mb-4" message="This signature will be appended to the end of outgoing emails sent via Relevize." />
        <Editor
          v-model="userNylasSignatureBody"
          :api-key="generalStore.tinyMceApiKey"
          :init="{
            width: 664,
            height: 444,
            statusbar: false,
            menu: {},
            menubar: false,
            image_advtab: true,
            plugins: 'lists link image table code wordcount',
            toolbar: 'bold italic underline link unlink | image buttonImgAlignleft buttonImgAlignRight | table code | undo redo',
            content_style: 'body { font-family: Arial, sans-serif; font-size: 10.5pt; } p { margin: 0; } ul { padding-inline-start: 30px !important; margin-bottom: 8px !important; margin-block-start: 8px; }',
            style_formats: [
              { title: 'Image', items: [
                {title: 'Image Left', selector: 'img', styles: {
                  'float' : 'left',
                  'margin': '0 10px 10px 0',
                }},
                {title: 'Image Right', selector: 'img', styles: {
                  'float' : 'right',
                  'margin': '0 0 10px 10px',
                }}
              ]}
            ],
            table_default_attributes: {
              border: '0'
            },
            table_sizing_mode: 'fixed',
            setup: editorSetup,
            paste_preprocess: handlePaste,
            images_upload_handler: handleUploadImage,
          }"
          @dirty="handleDirty"
        />
        <span class="caption2-desktop">{{ numCharsInSignature }} / {{ maxChars }} characters</span>
      </q-card-section>

      <q-card-actions align="right" class="q-pa-none row">
        <q-btn
          v-close-popup
          class="q-mr-sm big-button secondary"
          label="Cancel"
          data-test="CancelButton"
          @click="emit('cancel')"
        />
        <q-btn
          v-close-popup
          class="r-ma-0! big-button primary"
          label="Save"
          color="primary"
          data-test="OkButton"
          :disabled="!isDirty"
          @click="handleClickSave"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { defineEmits, toRefs, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useDialogPluginComponent } from 'quasar'
import Editor from '@tinymce/tinymce-vue'
import { UploadClient } from '@uploadcare/upload-client'

import { useUserStore } from '@stores/users'
import { useGeneralStore } from '@stores/general'

import { RelCardInfo } from '@/components-rel'

const { dialogRef, onDialogHide } = useDialogPluginComponent()
const emit = defineEmits([
  ...useDialogPluginComponent.emits,
  'cancel',
  'ok'
])

const uploadcareClient = new UploadClient({ publicKey: import.meta.env.VITE_UPLOAD_CARE_PUB_KEY })

const generalStore = useGeneralStore()
const userStore = useUserStore()
const { userNylasSignature } = toRefs(userStore)

const userNylasSignatureBody = ref('')
const isDirty = ref(false)
const maxChars = ref(2000)
const numCharsInSignature = ref(0)
let getCharacterCount

function handleDirty(_, editor) {
  isDirty.value = editor.isDirty()
}

async function editorSetup(editor) {
  editor.on('SetContent', () => {
    // Saving this for use outside of this function
    // since I haven't found a way to access the editor instance elsewhere.
    getCharacterCount = editor.plugins.wordcount.body.getCharacterCount
    numCharsInSignature.value = getCharacterCount()
  })
    
  editor.ui.registry.addIcon('formatImageLeft', '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M120-280v-400h400v400H120Zm80-80h240v-240H200v240Zm-80-400v-80h720v80H120Zm480 160v-80h240v80H600Zm0 160v-80h240v80H600Zm0 160v-80h240v80H600ZM120-120v-80h720v80H120Z"/></svg>')
  editor.ui.registry.addIcon('formatImageRight', '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M440-280v-400h400v400H440Zm80-80h240v-240H520v240ZM120-120v-80h720v80H120Zm0-160v-80h240v80H120Zm0-160v-80h240v80H120Zm0-160v-80h240v80H120Zm0-160v-80h720v80H120Z"/></svg>')

  editor.ui.registry.addButton('buttonImgAlignleft', {
    icon: 'formatImageLeft',
    tooltip: 'Left-align Image',
    onAction: function() {
      editor.formatter.apply('custom-image left')
    }
  })

  editor.ui.registry.addButton('buttonImgAlignRight', {
    icon: 'formatImageRight',
    tooltip: 'Right-align Image',
    onAction: function() {
      editor.formatter.apply('custom-image right')
    }
  })
}

watch(() => userNylasSignatureBody.value, () => {
  if (userNylasSignatureBody.value && getCharacterCount) {
    numCharsInSignature.value = getCharacterCount()
  }
})

function handlePaste(editor/*, args */) {
  editor.uploadImages().then(() => {
    // Do nothing?
  })
}

async function handleUploadImage(blobInfo/*, progress */) {
  return new Promise((resolve, reject) => {
    uploadcareClient.base(blobInfo.blob(), {}).then(data => {
      setTimeout(() => {
        // We seem to need to wait a bit for the image to be available.
        // In the meantime, tiny-mce will show a preview of the image in the editor
        // which seems to work seamlessly.
        resolve(`https://ucarecdn.com/${data.file}/`)
      }, 1000)
    }).catch(err => reject(err))
  })
}

async function handleClickSave() {
  await userStore.saveUserNylasSignature({ body: userNylasSignatureBody.value })
  emit('ok')
}

onMounted(async() => {
  userNylasSignatureBody.value = userNylasSignature.value?.body
})

onBeforeUnmount(() => {
  userNylasSignatureBody.value = ''
})
</script>

<style lang="scss">
.rel-dialog-edit-signature {
  position: relative;

  .q-card {
    width: 744px !important;
    height: 705px;
  }

  .loading {
    z-index: 10000;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(black, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.tox-notifications-container  {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.fullscreen {
  z-index: 1200;
}
textarea {
  display: none;
}
:deep(.tox-tinymce) {
  border: none;
  overflow: visible;
}
:deep(.tox-editor-header), :deep(.tox-sidebar-wrap) {
  border: 1px solid var(--sapphire) !important;
  border-radius: var(--radius);
  margin-bottom: 4px;
  box-shadow: none !important;
}
:deep(.tox .tox-edit-area::before) {
  border: none !important;
}
</style>